import {
  Procedure,
  CodeableConcept,
  Patient,
  Encounter,
} from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import { HL7 } from '@consts/systems.consts';

// =============================================================================
// TYPES
// =============================================================================

export type SurgeryInputItem = {
  id?: string;
  surgery: CodeableConcept | undefined;
  performedDate: Date | null;
  notes: string;
};

// =============================================================================
// GETTERS - Safe access to nested fields
// =============================================================================

export function getSurgeryCode(
  surgery: SurgeryInputItem
): CodeableConcept | undefined {
  return surgery.surgery;
}

export function getSurgeryCodeText(
  surgery: SurgeryInputItem
): string | undefined {
  const code = getSurgeryCode(surgery);
  return code?.text || code?.coding?.[0]?.display;
}

// =============================================================================
// FACTORY - Create new SurgeryInputItem
// =============================================================================

export function createNewSurgeryInputItem(): SurgeryInputItem {
  return {
    id: undefined,
    surgery: undefined,
    performedDate: null,
    notes: '',
  };
}

// =============================================================================
// HELPERS - Convenience functions
// =============================================================================

export function hasSurgeryCode(surgery: SurgeryInputItem): boolean {
  const code = getSurgeryCode(surgery);
  return Boolean(code?.coding?.[0]?.code || code?.text);
}

// =============================================================================
// MAPPING - Convert between Procedure and SurgeryInputItem
// =============================================================================

export function mapProcedureToSurgeryInput(
  procedure: Procedure
): SurgeryInputItem {
  let performedDate: Date | null = null;
  if (procedure.performedDateTime) {
    performedDate = new Date(procedure.performedDateTime);
  } else if (procedure.performedPeriod?.start) {
    performedDate = new Date(procedure.performedPeriod.start);
  }

  // Extract notes from procedure.note array (first note's text)
  const notes = procedure.note?.[0]?.text || '';

  return {
    id: procedure.id,
    surgery: procedure.code,
    performedDate,
    notes,
  };
}

export function mapProceduresToSurgeryInputs(
  procedures: Procedure[]
): SurgeryInputItem[] {
  return procedures.map(mapProcedureToSurgeryInput);
}

// =============================================================================
// PREPARATION - Prepare SurgeryInputItem for saving
// =============================================================================

export function prepareSurgeryForSave(
  patient: Patient,
  encounter: Encounter,
  surgeryInput: SurgeryInputItem,
  isMedicalHistory: boolean = false
): Procedure {
  const procedure: Procedure = {
    resourceType: 'Procedure',
    status: 'completed',
    subject: createReference(patient),
    encounter: createReference(encounter),
    code: surgeryInput.surgery,
    performedDateTime: surgeryInput.performedDate
      ? surgeryInput.performedDate.toISOString().split('T')[0]
      : undefined,
    ...(surgeryInput.id && { id: surgeryInput.id }),
  };

  // Add notes if provided
  if (surgeryInput.notes?.trim()) {
    procedure.note = [
      {
        text: surgeryInput.notes.trim(),
      },
    ];
  }

  if (isMedicalHistory) {
    procedure.category = {
      coding: [
        {
          system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
          code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.MEDICAL_HISTORY,
          display: 'Medical History',
        },
      ],
      text: 'Medical History',
    };
  }

  return procedure;
}
